import { useMsal } from "@azure/msal-react";
import { ChangeEvent, useEffect, useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import uuid from "react-uuid";
import { ThemeProvider } from "styled-components";

import { axiosPatchRequest, axiosPutRequest } from "../api/ApiClient";
import { ReactComponent as ActiveIcon } from "../assets/icons/Active.svg";
import { ReactComponent as BlockIcon } from "../assets/icons/Block.svg";
import { ReactComponent as LeftArrow } from "../assets/icons/LeftArrow.svg";
import { ReactComponent as RightArrow } from "../assets/icons/RightArrow.svg";
import { ReactComponent as SuccessModal } from "../assets/icons/SuccessModal.svg";
import { ReactComponent as UserIcon } from "../assets/icons/User.svg";
import Dialog from "../components/Dialog";
import InputText from "../components/InputText";
import UsersDTO from "../model/dto/UsersDTO";
import SelectOption from "../model/SelectOption";
import UserService from "../service/Docente/UserService";
import ButtonStyles from "../styles/components/ButtonStyles";
import TableStyles from "../styles/components/TableStyles";
import {
    StyledBody,
    StyledHeader,
    StyledSubTitle,
    StyledTitle,
} from "../styles/MainStyles";
import UploadFileStyles from "../styles/pages/UploadFilesStyles";
import UserManagementStyle from "../styles/pages/UserManagement";
import { MASK_DATE_BR } from "../utils/constants";
import GenericUtils from "../utils/GenericUtils";
import StringUtils from "../utils/types/StringUtils";
import { GenericValues } from "../model/dto/GenericValues";
import { User } from "../model/dto/User";
import { IPagination } from "../components/IPagination";

interface Option {
    value: any;
    label: string;
    key?: string;
    disabled?: boolean;
}

function UserManagement() {
    const initialMeta = {
        actualPage: 1,
        sizePage: 10,
        totalPages: 0,
        totalRecords: 0,
    } as IPagination;

    const { StyledImportButton, StyledModalTitle } = UploadFileStyles;
    const { StyledMultiSelect, StyledLabel } = UserManagementStyle;

    const [itensPerPage] = useState(10);
    const [profiles, setProfiles] = useState<any[]>([]);
    const [getProfilesUser, setGetProfilesUsers] = useState<any[]>([]);
    const [users, setUsers] = useState<GenericValues<User>>(
        {} as GenericValues<User>
    );
    const [inputName, setInputName] = useState("");
    const [selectProfile, setSelectProfile] = useState<any[]>([]);
    const [selectAcessProfile, setSelectAcessProfile] = useState<any[]>([]);
    const [seachParams, setSeachParams] = useState<UsersDTO>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [totalRecords, setTotalRecords] = useState(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [currentPage, setCurrentPage] = useState(0);
    const [meta, setMeta] = useState<IPagination>(initialMeta);
    const [inactiveModal, setInactiveModal] = useState(false);
    const [activeModal, setActiveModal] = useState(false);
    const [acessProfileModal, setAcessProfileModal] = useState(false);
    const [acessProfileSucessModal, setAcessProfileSucessModal] =
        useState(false);
    const [confirmInactiveModal, setConfirmInactiveModal] = useState(false);
    const [confirmActiveModal, setConfirmActiveModal] = useState(false);
    const [userIdValue, setUserIdValue] = useState<number | null>(null);

    const { t } = useTranslation();

    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();
    const theme = {};

    function clearPagination(_response?: any) {
        setSeachParams(undefined);
        setMeta(initialMeta);
        setCurrentPage(0);
    }

    async function getProfiles() {
        const response = await UserService.getProfiles(instance);

        if (GenericUtils.isSuccessStatus(response)) {
            const sortResponseList = response?.data?.sort((a: any, b: any) =>
                a.nomePerfil.localeCompare(b.nomePerfil)
            );
            const optionList: SelectOption[] = sortResponseList.map(
                (data: any) => {
                    return {
                        value: data.idPerfil,
                        label: data.nomePerfil,
                    };
                }
            );

            setGetProfilesUsers(sortResponseList);
            setProfiles(optionList);
        }
    }

    async function patchUsers(userId: number, status: boolean) {
        const params = {
            status,
        };
        await axiosPatchRequest(
            `/docente/v1/usuarios/${userId}`,
            params,
            acessToken,
            instance
        );
    }

    async function putUsers(userId: number) {
        const userMap = getProfilesUser.filter((user) => {
            return selectAcessProfile.some(
                (profile) => user.idPerfil === profile.value
            );
        });

        await axiosPutRequest(
            `/docente/v1/usuarios/${userId}/vinculo-perfis`,
            userMap,
            acessToken,
            instance
        );
    }

    function updatePagination(response: GenericValues<User>) {
        if (response.meta) {
            setMeta(response.meta);
        }
    }

    const getQueryFromDto = (userDto: UsersDTO): string => {
        if (userDto.nome_usuario && userDto.perfis) {
            return `&query=nome starts "${userDto.nome_usuario}" and listaPerfis(idPerfil in (${userDto.perfis}))`;
        }
        if (userDto.nome_usuario) {
            return `&query=nome starts "${userDto.nome_usuario}"`;
        }
        if (userDto.perfis) {
            return `&query=listaPerfis(idPerfil in (${userDto.perfis}))`;
        }
        return "";
    };

    async function getUsers(dto: UsersDTO) {
        const queryParam = getQueryFromDto(dto);

        const response = await UserService.getUsers(dto, queryParam, instance);

        if (response.status === 200 && response?.data) {
            updatePagination(response.data as GenericValues<User>);
            setUsers(response.data as GenericValues<User>);
        } else {
            setUsers({} as GenericValues<User>);
            clearPagination();
        }
    }

    useEffect(() => {
        const dto: UsersDTO = {
            _limit: String(itensPerPage),
            asc: "true",
            desc: "false",
            recisao: "false",
            _offset: "1",
            nome_usuario: null,
            perfis: null,
        };
        getUsers(dto);
        getProfiles();
    }, []);

    const handleInputNameChange = (event: ChangeEvent<{ value: unknown }>) => {
        setInputName(event.target.value as string);
    };

    function handleSearch(searchAll: boolean) {
        let profile = selectProfile.map((item) => item.value);
        let inputNameValue = inputName;
        if (searchAll) {
            profile = [];
            inputNameValue = "";
        }

        const dto: UsersDTO = {
            _limit: String(itensPerPage),
            asc: "true",
            desc: "false",
            recisao: "false",
            _offset: "1",
            nome_usuario: inputNameValue,
            perfis: profile.toString() || null,
        };

        setSeachParams(dto);
        getUsers(dto);
    }

    function handleCurrentPaginate(page: string) {
        if (meta.actualPage === 1) {
            return;
        }

        const dto: UsersDTO = {
            _limit: String(itensPerPage),
            asc: "true",
            desc: "false",
            recisao: "false",
            _offset: page || String(meta.actualPage),
            nome_usuario: seachParams?.nome_usuario || "",
            perfis: seachParams?.perfis || null,
        };
        setSeachParams(dto);
        getUsers(dto);
    }

    function handleNextPaginate(page: string) {
        if (meta && meta.actualPage === meta.totalPages) {
            return;
        }
        const dto: UsersDTO = {
            _limit: String(itensPerPage),
            asc: "true",
            desc: "false",
            recisao: "false",
            _offset: page || String(meta.actualPage),
            nome_usuario: seachParams?.nome_usuario || "",
            perfis: seachParams?.perfis || null,
        };
        setSeachParams(dto);
        getUsers(dto);
    }

    function handleClearFilter() {
        setSelectProfile([]);
        setInputName("");
    }

    const handleInactive = (userId: number) => {
        setUserIdValue(userId);
        setInactiveModal(true);
    };

    const handleActive = (userId: number) => {
        setUserIdValue(userId);
        setActiveModal(true);
    };

    const handleAcessProfile = (userId: number) => {
        const perfis: Option[] = [];
        const findUser = users.items.find((user) => user.docenteId === userId);

        findUser?.perfis?.map((te: any) =>
            perfis.push({
                value: te.idPerfil,
                label: te.nomePerfil,
            })
        );
        setSelectAcessProfile(perfis);
        setAcessProfileModal(true);
        setUserIdValue(userId);
    };

    const handleClose = () => {
        setInactiveModal(false);
        setActiveModal(false);
        setConfirmInactiveModal(false);
        setConfirmActiveModal(false);
        setAcessProfileModal(false);
        setAcessProfileSucessModal(false);
        setUserIdValue(null);
    };

    const handleAcessProfileClose = () => {
        if (userIdValue) putUsers(userIdValue).then(() => handleSearch(false));
        setAcessProfileModal(false);
        setAcessProfileSucessModal(true);
        setSelectAcessProfile([]);
    };

    const handleCloseInactiveConfirm = () => {
        if (userIdValue) {
            patchUsers(userIdValue, false).then(() => handleSearch(false));
        }
        setInactiveModal(false);
        setConfirmInactiveModal(true);
    };

    const handleCloseActiveConfirm = () => {
        if (userIdValue) {
            patchUsers(userIdValue, true).then(() => handleSearch(false));
        }
        setActiveModal(false);
        setConfirmActiveModal(true);
    };

    const customValueRenderer = (selected: Option[]) => {
        return selected.length
            ? selected.map(({ label }) => `${label}, `)
            : "Perfil";
    };

    const customEditValueRenderer = (selected: Option[]) => {
        return selected.length
            ? selected.map(({ label }) => `${label}, `)
            : "Selecione o perfil de acesso";
    };

    const DialogInactiveConfirm = (
        <Dialog openModal={inactiveModal} onHide={handleClose}>
            <StyledModalTitle>
                Tem certeza que deseja tornar inativo o perfil do usuário?
            </StyledModalTitle>

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleCloseInactiveConfirm}
            >
                Inativar
            </StyledImportButton>
        </Dialog>
    );

    const DialogActiveConfirm = (
        <Dialog openModal={activeModal} onHide={handleClose}>
            <StyledModalTitle>
                Tem certeza que deseja tornar ativo o perfil do usuário?
            </StyledModalTitle>

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleCloseActiveConfirm}
            >
                Ativar
            </StyledImportButton>
        </Dialog>
    );

    const DialogInactiveStatusConfirm = (
        <Dialog
            openModal={confirmInactiveModal}
            onHide={handleClose}
            modalIcon={<SuccessModal />}
        >
            <StyledModalTitle>Usuário inativado</StyledModalTitle>

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleClose}
            >
                ok
            </StyledImportButton>
        </Dialog>
    );

    const DialogActiveStatusConfirm = (
        <Dialog
            openModal={confirmActiveModal}
            onHide={handleClose}
            modalIcon={<SuccessModal />}
        >
            <StyledModalTitle>Usuário ativado</StyledModalTitle>

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleClose}
            >
                ok
            </StyledImportButton>
        </Dialog>
    );

    const DialogSucessEdit = (
        <Dialog
            openModal={acessProfileSucessModal}
            onHide={handleClose}
            modalIcon={<SuccessModal />}
        >
            <StyledModalTitle>Perfil editado</StyledModalTitle>

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleClose}
            >
                ok
            </StyledImportButton>
        </Dialog>
    );

    const DialogAcessProfile = (
        <Dialog openModal={acessProfileModal} onHide={handleClose}>
            <StyledModalTitle>Editar perfil de acesso</StyledModalTitle>
            <StyledMultiSelect
                options={profiles}
                value={selectAcessProfile}
                onChange={setSelectAcessProfile}
                labelledBy="Perfil"
                disableSearch
                valueRenderer={customEditValueRenderer}
                hasSelectAll={false}
            />

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleAcessProfileClose}
            >
                Salvar
            </StyledImportButton>
        </Dialog>
    );

    const actionsTable = (ativo: boolean, userId: number) => {
        const renderTooltipActive = (props: any) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tooltip {...props}>Ativar</Tooltip>
        );

        const renderTooltipInactivate = (props: any) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tooltip {...props}>Inativar</Tooltip>
        );

        const renderTooltipProfile = (props: any) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tooltip {...props}>Perfil de acesso</Tooltip>
        );

        return (
            <div className="d-grid justify-content-center">
                <Row>
                    <Col xs="4">
                        <OverlayTrigger
                            placement="auto"
                            overlay={renderTooltipProfile}
                        >
                            <ButtonStyles.BtnTable
                                onClick={() => handleAcessProfile(userId)}
                                variant="link"
                            >
                                <UserIcon style={{ fill: "#404040" }} />
                            </ButtonStyles.BtnTable>
                        </OverlayTrigger>
                    </Col>
                    <Col xs="4">
                        <OverlayTrigger
                            placement="auto"
                            overlay={renderTooltipActive}
                        >
                            <ButtonStyles.BtnTable
                                onClick={() => handleActive(userId)}
                                variant="link"
                                disabled={ativo}
                            >
                                <ActiveIcon
                                    style={{
                                        fill: !ativo ? "#404040" : "#E4E4E4",
                                    }}
                                />
                            </ButtonStyles.BtnTable>
                        </OverlayTrigger>
                    </Col>
                    <Col xs="4">
                        <OverlayTrigger
                            placement="auto"
                            overlay={renderTooltipInactivate}
                        >
                            <ButtonStyles.BtnTable
                                onClick={() => handleInactive(userId)}
                                variant="link"
                                disabled={!ativo}
                            >
                                <BlockIcon
                                    style={{
                                        fill: ativo ? "#404040" : "#E4E4E4",
                                    }}
                                />
                            </ButtonStyles.BtnTable>
                        </OverlayTrigger>
                    </Col>
                </Row>
            </div>
        );
    };

    const pagination = meta && (
        <div
            style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                height: "74px",
                borderWidth: "0 1px 1px 1px",
                borderColor: "#E4E4E4",
                borderStyle: "solid",
            }}
        >
            <StyledLabel>Itens por pagina: {meta.sizePage}</StyledLabel>
            <StyledLabel>
                {meta.actualPage} - {meta.totalPages} de {meta.totalRecords}
            </StyledLabel>
            <LeftArrow
                style={{
                    cursor: meta.actualPage === 1 ? "default" : "pointer",
                    fill: meta.actualPage === 1 ? "#C4A0FF" : "#9654FF",
                    marginRight: "12px",
                }}
                onClick={() =>
                    handleCurrentPaginate(String(meta.actualPage - 1))
                }
            />
            <RightArrow
                style={{
                    cursor:
                        meta.actualPage === meta.totalPages
                            ? "default"
                            : "pointer",
                    fill:
                        meta.actualPage === meta.totalPages
                            ? "#C4A0FF"
                            : "#9654FF",
                    marginLeft: "12px",
                    marginRight: "16px",
                }}
                onClick={() => handleNextPaginate(String(meta.actualPage + 1))}
            />
        </div>
    );

    const search = (event: any) => {
        event.preventDefault();
        handleSearch(false);
    };

    return (
        <ThemeProvider theme={theme}>
            <StyledHeader>
                <StyledTitle>Gestão de Usuários</StyledTitle>
                <StyledSubTitle>
                    Use esse espaço para editar o perfil dos colaboradores
                </StyledSubTitle>
            </StyledHeader>
            <StyledBody>
                <form onSubmit={search}>
                    <Row className="mt-3">
                        <Col>
                            <StyledSubTitle>
                                Utilize os filtros abaixo para filtrar por
                                usuário ou por perfil de colaborador
                            </StyledSubTitle>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" md="5" lg="5">
                            <InputText
                                placeholder="Pesquise aqui"
                                value={inputName}
                                onChange={handleInputNameChange}
                            />
                        </Col>
                        <Col sm="12" md="3" lg="3">
                            <StyledMultiSelect
                                options={profiles}
                                value={selectProfile}
                                onChange={setSelectProfile}
                                labelledBy="Perfil"
                                disableSearch
                                valueRenderer={customValueRenderer}
                                hasSelectAll={false}
                            />
                        </Col>
                        <Col xs="6" md="auto" lg="auto">
                            <ButtonStyles.BtnPrimary
                                style={{ width: 116 }}
                                type="submit"
                            >
                                Filtrar
                            </ButtonStyles.BtnPrimary>
                        </Col>
                        <Col xs="6" md="auto" lg="auto">
                            <ButtonStyles.BtnOutlinePrimary
                                style={{ width: 116 }}
                                onClick={() => {
                                    handleClearFilter();
                                    handleSearch(true);
                                }}
                            >
                                Limpar
                            </ButtonStyles.BtnOutlinePrimary>
                        </Col>
                    </Row>
                    <Row
                        className="mt-3"
                        style={{ marginTop: 40, marginBottom: 18 }}
                    >
                        <Col>
                            <TableStyles.TitleTable>
                                Todos os usuários
                            </TableStyles.TitleTable>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TableStyles.TablePrimary
                                bordered
                                striped
                                responsive
                                style={{ marginBottom: 0 }}
                            >
                                <thead>
                                    <tr>
                                        <th>Nome completo</th>
                                        <th>Matrícula</th>
                                        <th>Perfil</th>
                                        <th>Última modificação</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.items?.length === 0 ? (
                                        <tr
                                            key={uuid()}
                                            className="table-empty"
                                        >
                                            <td colSpan={7}>
                                                {StringUtils.capitalizeFirstLetter(
                                                    t("tableEmpty")
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        users.items?.map((item) => (
                                            <tr key={uuid()}>
                                                <td>{item.nome}</td>
                                                <td>{item.matricula}</td>
                                                <td>
                                                    {item?.perfis?.length > 0
                                                        ? item.perfis
                                                              .map(
                                                                  (
                                                                      perfil: any
                                                                  ) =>
                                                                      perfil.nomePerfil
                                                              )
                                                              .join(", ")
                                                        : "Não atribuído"}
                                                </td>
                                                <td>
                                                    {StringUtils.formatDateString(
                                                        item.dataUpdate,
                                                        MASK_DATE_BR
                                                    )}
                                                </td>
                                                <td>
                                                    {actionsTable(
                                                        item.ativo,
                                                        item.docenteId
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </TableStyles.TablePrimary>
                        </Col>
                    </Row>
                </form>
            </StyledBody>
            {pagination}
            {DialogInactiveConfirm}
            {DialogActiveConfirm}
            {DialogInactiveStatusConfirm}
            {DialogActiveStatusConfirm}
            {DialogAcessProfile}
            {DialogSucessEdit}
        </ThemeProvider>
    );
}

export default UserManagement;
